import { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useAolStore } from '../../../context/AolContext';
import { PageType } from '../../../types/Page';
import { NoonReportStatusEnum, NoonReportTypeEnum } from '../../../types/ViewModel/AolEnums';
import { AolDataItem, AolGroup } from '../../../types/ViewModel/AolItem';
import {
    formatAolFromToTimeUrl,
    formatAolUrlDate,
    getAolDateFromUrlFormat,
    getAolFromToTimeFromUrlFormat,
    getAolGroupDate,
} from '../../../utils/aol/aolDateUtils';
import { aolFilterNoonReportStatus, aolHoursText, aolTypeDescriptionText, hasBeenMrvVerified } from '../../../utils/aolSectionHelper';
import useAolSelectionHelper from '../../../utils/useAolSelectionHelper';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import StatusIcon from '../../ui/status-icon/NoonReportStatusIcon';
import AolFilterHeaderComponent from '../aol-filter-header/AolFilterHeader';
import AolMetaIconsComponent from '../aol-meta-icons/AolMetaIcons';
import { AolListItem, AolListItemWrapper, TabListButton, TabsWrapper, Wrapper } from './AolListComponent.style';
import useGetAolData from '../../../api/aol/useGetAolData';
import { useVesselStore } from '../../../context/VesselContext';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateSame } from 'odfjell-date-time-utils/dateTimeUtils';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import { useAppStore } from 'src/context/AppContext';
import { AolPageEnum } from 'src/enums/aol/AolPageEnum';
import { RedIcon } from 'src/components/ui/status-icon/VerificationIcon.styles';

type Props = {
    page: PageType;
};

type PageFilteredGroup = {
    approvedAols: AolGroup[];
    completedAols: AolGroup[];
    missingEditedAols: AolGroup[];
};

const AolListComponent: React.FC<Props> = (props: Props) => {
    const {
        selectedAolGroup,
        setSelectedAolGroup,
        setSelectedAolDataItem,
        aolGroups,
        isUpdatingAolGroupStatus,
        previousAolGroup,
        setPreviousAolGroup,
        setIsUpdatingAolGroupStatus,
        setAolGroupStatusMessage,
        aolListSortAsc,
        setAolFilter,
        setActiveAolPage,
        aolFilter,
    } = useAolStore();

    const { numMissingReportAol, numMissingApprovedAol } = useAppStore();

    const { selectedVessel } = useVesselStore();
    const { onSelectAolGroup } = useAolSelectionHelper();
    const [filteredAolGroups, setFilteredAolGroups] = useState<AolGroup[]>([]);
    const { isLoadingType } = useApiLoaderHelper();

    const isSavingAolSection = isLoadingType('saving_section');
    const { getAolData } = useGetAolData();
    const { triggerQueryParamsUpdate, queryParamUpdate, getQueryParamValueFromUrl } = useQueryParamsHandler();

    useEffect(() => {
        if (!props.page) return;

        const filterResult = filterAolGroups();

        if (props.page === 'reporting') {
            const _filteredAolGroups = filterResult.pageFilteredAolGroups.missingEditedAols ?? [];
            setFilteredAolGroups(sortAolGroups([..._filteredAolGroups]));
            setSelectedGroupToLastInList(_filteredAolGroups, false);
            setActiveAolPage(AolPageEnum.Reporting);
        } else if (props.page === 'approval') {
            const _filteredAolGroups = filterResult.pageFilteredAolGroups.completedAols ?? [];
            setFilteredAolGroups(sortAolGroups([..._filteredAolGroups]));
            setSelectedGroupToLastInList(_filteredAolGroups, false);
            setActiveAolPage(AolPageEnum.Approval);
        } else if (props.page === 'approved') {
            const _filteredAolGroups = filterResult.pageFilteredAolGroups.approvedAols ?? [];
            setFilteredAolGroups(sortAolGroups([..._filteredAolGroups]));
            setSelectedGroupToLastInList(_filteredAolGroups, true);
            setActiveAolPage(2);
        }
    }, [props.page, aolFilter?.fromDate]);

    useEffect(() => {
        if (!queryParamUpdate || !aolGroups || aolGroups.length === 0) return;

        const ls_userChangedVessel = sessionStorage.getItem('userChangedVessel');
        if (ls_userChangedVessel !== 'true') selectAolGroupFromQuery();
    }, [queryParamUpdate]);

    useEffect(() => {
        if (!aolGroups || aolGroups.length === 0) {
            setFilteredAolGroups([]);
            return;
        }

        setSelectedAolGroup(null);
        setSelectedAolDataItem(null);

        if (isUpdatingAolGroupStatus) {
            if (previousAolGroup) {
                updateQuery(previousAolGroup, previousAolGroup.aolDataItems[0]);
                setPreviousAolGroup(null);
            }
            setTimeout(() => {
                setAolGroupStatusMessage(null);
            }, 6000);

            setIsUpdatingAolGroupStatus(false);
        }

        const filterResult = filterAolGroups();

        setDataFromQuery(filterResult.filteredAolGroups);
    }, [aolGroups, isUpdatingAolGroupStatus]);

    useEffect(() => {
        const filterResult = filterAolGroups();
        setFilteredAolGroups(sortAolGroups([...filterResult.filteredAolGroups]));
    }, [aolListSortAsc]);

    const setDataFromQuery = (_filteredAolGroups: AolGroup[]) => {
        const aolDateStr = getQueryParamValueFromUrl('aolDate');
        const fromTimeStr = getQueryParamValueFromUrl('fromTime');
        const toTimeStr = getQueryParamValueFromUrl('toTime');

        if (aolDateStr) {
            const aolDate = getAolDateFromUrlFormat(aolDateStr);
            const selectedAolGroup = _filteredAolGroups.find((a) => isDateSame(getAolGroupDate(a), aolDate, 'day'));
            if (selectedAolGroup) {
                setSelectedAolGroup({ ...selectedAolGroup });
                updatePreviousAolGroup(selectedAolGroup);

                if (fromTimeStr && toTimeStr) {
                    const fromTime = getAolFromToTimeFromUrlFormat(fromTimeStr);
                    const toTime = getAolFromToTimeFromUrlFormat(toTimeStr);
                    const selectedAolDataItem = selectedAolGroup.aolDataItems.find(
                        (a) => isDateSame(a.reportFromTimeLocalTime, fromTime, 'minute') && isDateSame(a.reportToTimeLocalTime, toTime, 'minute')
                    );

                    if (selectedAolDataItem) {
                        setSelectedAolDataItem({ ...selectedAolDataItem });
                    }
                } else {
                    setSelectedAolDataItem(Math.max.apply(Math, ...selectedAolGroup.aolDataItems.map((x) => x.reportDate)));
                }
            }
        }
    };

    const selectAolGroupFromQuery = () => {
        const aolDateStr = getQueryParamValueFromUrl('aolDate');
        const fromTimeStr = getQueryParamValueFromUrl('fromTime');
        const toTimeStr = getQueryParamValueFromUrl('toTime');
        const indexInDayStr = getQueryParamValueFromUrl('indexInDay');

        if (aolDateStr) {
            const aolDate = getAolDateFromUrlFormat(aolDateStr);
            const selectedAolGroup = filteredAolGroups.find((a) => isDateSame(getAolGroupDate(a), aolDate, 'day'));
            if (selectedAolGroup) {
                setSelectedAolGroup({ ...selectedAolGroup });
                updatePreviousAolGroup(selectedAolGroup);

                selectAolDataItem(selectedAolGroup, fromTimeStr, toTimeStr, indexInDayStr);
            } else {
                if (filteredAolGroups[0]) {
                    onSelectAolGroup(filteredAolGroups[0]);
                }
            }
        } else {
            if (filteredAolGroups[0]) {
                onSelectAolGroup(filteredAolGroups[0]);
            }
        }
    };

    const selectAolDataItem = (_selectedAolGroup: AolGroup, fromTimeStr: string | null, toTimeStr: string | null, indexInDayStr: string | null) => {
        let aolDataItem: AolDataItem | undefined = { ..._selectedAolGroup.aolDataItems[0] };
        if (fromTimeStr && toTimeStr) {
            const fromTime = getAolFromToTimeFromUrlFormat(fromTimeStr);
            const toTime = getAolFromToTimeFromUrlFormat(toTimeStr);
            aolDataItem = _selectedAolGroup.aolDataItems.find(
                (a) => isDateSame(a.reportFromTimeLocalTime, fromTime, 'minute') && isDateSame(a.reportToTimeLocalTime, toTime, 'minute')
            );
        } else if (indexInDayStr) {
            aolDataItem = _selectedAolGroup.aolDataItems.find((a) => a.noonReport && a.noonReport.metadata.indexInDay === +indexInDayStr);
        }

        if (aolDataItem) {
            setSelectedAolDataItem({ ...aolDataItem });
        }
    };

    const updatePreviousAolGroup = (selectedAolGroup: AolGroup) => {
        const previousAolGroup = [...aolGroups]
            .sort((a, b) => (a.aolDate > b.aolDate ? -1 : 1))
            .find((group) => group.aolDate < selectedAolGroup.aolDate && group.aolHours > 0);
        setPreviousAolGroup(previousAolGroup ?? null);
    };

    const updateQuery = (aolGroup: AolGroup, aolDataItem: AolDataItem) => {
        const queryParamValues: QueryParamValueProps[] = [];
        queryParamValues.push({ name: 'aolDate', newValue: formatAolUrlDate(getAolGroupDate(aolGroup)) });
        queryParamValues.push({ name: 'fromTime', newValue: formatAolFromToTimeUrl(aolDataItem.reportFromTimeLocalTime) });
        queryParamValues.push({ name: 'toTime', newValue: formatAolFromToTimeUrl(aolDataItem.reportToTimeLocalTime) });
        triggerQueryParamsUpdate(queryParamValues);
    };

    const sortAolGroups = (aolGroups): AolGroup[] => {
        if (aolListSortAsc) {
            return [...aolGroups].sort((a, b) => (a.aolDate > b.aolDate ? -1 : 1));
        } else {
            return [...aolGroups].sort((a, b) => (a.aolDate < b.aolDate ? -1 : 1));
        }
    };

    const filterAolGroups = () => {
        const _pageFilteredAolGroups = getFilteredAolGroupByPageArray();
        const _filteredAolGroups = sortAolGroups(getAolGroupsForPageArray(_pageFilteredAolGroups));
        setFilteredAolGroups([..._filteredAolGroups]);

        return { filteredAolGroups: _filteredAolGroups, pageFilteredAolGroups: _pageFilteredAolGroups };
    };

    const getFilteredAolGroupByPageArray = (): PageFilteredGroup => {
        return {
            missingEditedAols: [...aolGroups.filter((a) => aolFilterNoonReportStatus('reporting', a.status, a.aolDate, aolFilter.fromDate))],
            completedAols: [...aolGroups.filter((a) => aolFilterNoonReportStatus('approval', a.status, a.aolDate, aolFilter.fromDate))],
            approvedAols: [...aolGroups.filter((a) => aolFilterNoonReportStatus('approved', a.status, a.aolDate, aolFilter.fromDate))],
        };
    };

    const getAolGroupsForPageArray = (aolPageGrouped: PageFilteredGroup) => {
        let _filteredAolGroups: AolGroup[] = [];
        if (props.page === 'reporting') {
            _filteredAolGroups = aolPageGrouped.missingEditedAols;
        } else if (props.page === 'approval') {
            _filteredAolGroups = aolPageGrouped.completedAols;
        } else if (props.page === 'approved') {
            _filteredAolGroups = aolPageGrouped.approvedAols;
        }

        return _filteredAolGroups;
    };

    const showMenuTab = (page: PageType) => {
        const queryParamValues: QueryParamValueProps[] = [{ name: 'page', newValue: page }];
        triggerQueryParamsUpdate(queryParamValues);
    };

    const setSelectedGroupToLastInList = (aolGroupsInput: AolGroup[], showApproved: boolean) => {
        const aolGroupsAfterButtonFilter = aolGroupsInput.filter((o) => showAolGroup(o, showApproved));
        if (aolGroupsAfterButtonFilter && aolGroupsAfterButtonFilter.length > 0) {
            const lastAolGroup = aolGroupsAfterButtonFilter[aolGroupsAfterButtonFilter.length - 1];
            const lastAolDataItem = aolListSortAsc
                ? lastAolGroup.aolDataItems.length > 0
                    ? lastAolGroup.aolDataItems[lastAolGroup.aolDataItems.length - 1]
                    : lastAolGroup.aolDataItems[0]
                : lastAolGroup.aolDataItems[0];

            updateQuery(lastAolGroup, lastAolDataItem);
        } else {
            setSelectedAolGroup(null);
            setSelectedAolDataItem(null);
        }
    };

    const showAolGroup = (aolGroup: AolGroup, showApproved: boolean) => {
        return (
            (aolGroup.status === NoonReportStatusEnum.Approved && showApproved) ||
            (aolGroup.status !== NoonReportStatusEnum.Approved && !showApproved)
        );
    };

    const onSelectAolGroupClick = (aolGroup: AolGroup) => {
        if (!isSavingAolSection) onSelectAolGroup(aolGroup);
    };
    const AolGroupElement = (aolGroup: AolGroup) => {
        return (
            <AolListItemWrapper onClick={() => onSelectAolGroupClick(aolGroup)}>
                <div className="aol-item">{AolListElement(aolGroup)}</div>
            </AolListItemWrapper>
        );
    };

    const AolListElement = (aolGroupItem: AolGroup) => {
        const isSelected = selectedAolGroup && isDateSame(getAolGroupDate(selectedAolGroup), getAolGroupDate(aolGroupItem), 'day');
        const reportType = aolGroupItem.aolDataItems[aolGroupItem.aolDataItems.length - 1]?.reportType ?? NoonReportTypeEnum.Sea;
        const isValid = aolGroupItem.isValid;

        return (
            <AolListItem
                isSelected={isSelected}
                isValid={isValid}
                noonReportType={reportType}
                noonReportStatus={aolGroupItem.status}
                key={'aolGroupItem_aolDate_' + aolGroupItem.aolDate}
            >
                <div className="upper-wrapper">
                    <span>{prettifyDate(getAolGroupDate(aolGroupItem))}</span>
                    <span className="icons-wrapper">
                        <span className="icon-wrapper">
                            <StatusIcon reportStatus={aolGroupItem.status} />
                        </span>
                    </span>
                </div>
                <div className="lower-wrapper">
                    <span>{aolTypeDescriptionText(aolGroupItem)}</span>
                    <span>{aolHoursText(aolGroupItem)}</span>
                    <AolMetaIconsComponent
                        aolVersion={aolGroupItem.aolVersion}
                        hasTankersEventMismatch={aolGroupItem.hasTankersEventMismatch}
                        isMrvVerified={hasBeenMrvVerified(selectedVessel?.mrvVerifiedUntilDate, aolGroupItem?.aolDate)}
                        isValid={aolGroupItem.isValid}
                    />
                </div>
            </AolListItem>
        );
    };

    const selectAolItem = (fromDate: Date, toDate: Date) => {
        getAolData(selectedVessel!.imoNumber, formatAolUrlDate(fromDate), formatAolUrlDate(toDate));
        sessionStorage.setItem('userChangedFilter', 'true');
        setAolFilter({ fromDate, toDate, isDefault: false });
    };

    return (
        <Wrapper page={props.page}>
            <AolFilterHeaderComponent page={props.page} onChange={selectAolItem}></AolFilterHeaderComponent>
            <div className="aol-list-tabs">
                <TabsWrapper className="full-width">
                    <TabListButton
                        className="tab full-width  text-overflow"
                        isSelected={props.page === 'reporting'}
                        onClick={() => showMenuTab('reporting')}
                        title="Reporting"
                    >
                        Reporting
                        {numMissingReportAol && (
                            <RedIcon className="warning-icon" title={'Missing/uncompleted tasks last 14 days'}>
                                {numMissingReportAol}
                            </RedIcon>
                        )}
                    </TabListButton>
                    <TabListButton
                        className="tab full-width text-overflow"
                        isSelected={props.page === 'approval'}
                        onClick={() => showMenuTab('approval')}
                        title="To be approved"
                    >
                        To be approved
                        {numMissingApprovedAol && (
                            <RedIcon className="warning-icon" title={'Missing/uncompleted tasks last 14 days'}>
                                {numMissingApprovedAol}
                            </RedIcon>
                        )}
                    </TabListButton>
                    <TabListButton
                        className="tab full-width  text-overflow"
                        isSelected={props.page === 'approved'}
                        onClick={() => showMenuTab('approved')}
                        title="Approved"
                    >
                        Approved
                    </TabListButton>
                </TabsWrapper>
            </div>

            <div className="aol-list">
                {filteredAolGroups && filteredAolGroups.length > 0 && (
                    <>
                        {filteredAolGroups.map((aolGroup) => {
                            return <div key={aolGroup.aolDate}>{AolGroupElement(aolGroup)}</div>;
                        })}
                    </>
                )}
            </div>
        </Wrapper>
    );
};

export default AolListComponent;
